import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { Check, Search, X } from "lucide-react";
import { Spinner, Text, TextField, TextFieldVariant } from "@merge-api/merge-javascript-shared";
import clsx from "clsx";
import { FieldMappingCreationAndEditDict } from "../../../../../../../../models/Entities";
import FieldMappingDropdownChild from "../../../../../../../shared/FieldMappingDropdownChild";
import { useRemoteFieldOptionFilter } from "../../../utils/remoteFieldSearch";

interface Props {
  options: FieldMappingCreationAndEditDict[];
  value: FieldMappingCreationAndEditDict | null;
  onChange: (option: FieldMappingCreationAndEditDict | null) => void;
  placeholder?: string;
  loading?: boolean;
  loadingText?: string;
  noOptionsText?: string;
  matchCommonModel?: boolean;
}

// Fixed row height keeps every item's offset constant so the scroll position never shifts as the
// user scrolls, which is what caused the "bouncing" in the VariableSizeList + resetAfterIndex
// approach. Content taller than this is clipped rather than re-measured.
const ROW_HEIGHT = 116;
const LIST_MAX_HEIGHT = 348;
const OVERSCAN = 8;

const RemoteFieldTypeaheadV2 = ({
  options,
  value,
  onChange,
  placeholder,
  loading = false,
  loadingText = "Loading fields...",
  noOptionsText = "No remote fields available",
  matchCommonModel = false,
}: Props) => {
  const [filterValue, setFilterValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  // -1 means no row is keyboard/hover-active; a row only becomes active on hover or ArrowUp/Down,
  // so nothing is pre-highlighted when the dropdown opens.
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<FixedSizeList>(null);

  const filterOptions = useRemoteFieldOptionFilter(options, matchCommonModel);
  const filteredOptions = useMemo(() => filterOptions(filterValue), [filterOptions, filterValue]);

  const listHeight = Math.min(LIST_MAX_HEIGHT, filteredOptions.length * ROW_HEIGHT);
  const inputValue = isOpen && isSearching ? filterValue : value?.display_name ?? "";

  const selectedIndex = useMemo(
    () => (value ? filteredOptions.findIndex((option) => option.key === value.key) : -1),
    [value, filteredOptions],
  );
  // Open the list scrolled to the currently selected field (centered) instead of at the top. The
  // list remounts each time the dropdown opens, so react-window re-applies this on every open.
  const initialScrollOffset =
    selectedIndex >= 0
      ? Math.max(0, selectedIndex * ROW_HEIGHT - (LIST_MAX_HEIGHT - ROW_HEIGHT) / 2)
      : 0;

  // On a new search / filter, clear the active row and jump back to the top of the results.
  useEffect(() => {
    setActiveIndex(-1);
    listRef.current?.scrollTo(0);
  }, [filteredOptions]);

  // Clear the active row whenever the dropdown opens or closes so nothing is pre-highlighted; the
  // open scroll position is handled by initialScrollOffset on the list.
  useEffect(() => {
    setActiveIndex(-1);
  }, [isOpen]);

  // Close the dropdown and clear the in-progress search when clicking outside the component.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setFilterValue("");
        setIsSearching(false);
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // event handlers
  const handleSelect = useCallback(
    (option: FieldMappingCreationAndEditDict) => {
      onChange(option);
      setFilterValue("");
      setIsSearching(false);
      setIsOpen(false);
    },
    [onChange],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (event.key === "ArrowDown" || event.key === "Enter") {
        setIsOpen(true);
      }
      return;
    }
    // Before the user has moved the highlight, start keyboard nav from the selected row (if any)
    // so the first arrow continues from where the list opened rather than jumping to the top.
    const current = activeIndex >= 0 ? activeIndex : selectedIndex;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = Math.min(filteredOptions.length - 1, current + 1);
      setActiveIndex(next);
      listRef.current?.scrollToItem(next);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const next = Math.max(0, current - 1);
      setActiveIndex(next);
      listRef.current?.scrollToItem(next);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = filteredOptions[activeIndex];
      if (option) {
        handleSelect(option);
      }
    } else if (event.key === "Escape") {
      setFilterValue("");
      setIsSearching(false);
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const Row = useCallback(
    ({ index, style }: ListChildComponentProps) => {
      const option = filteredOptions[index];
      const isActive = index === activeIndex;
      const isSelected = option.key === value?.key;
      return (
        <div
          style={style}
          className={clsx(
            "flex items-start gap-x-2 overflow-hidden p-3 cursor-pointer",
            isActive && "bg-gray-10",
          )}
          onClick={() => handleSelect(option)}
          onMouseEnter={() => setActiveIndex(index)}
        >
          <div className="min-w-0 flex-1">
            <FieldMappingDropdownChild option={option} clampDisplayName />
          </div>
          {isSelected && <Check size={16} className="mt-0.5 shrink-0 text-blue-60" />}
        </div>
      );
    },
    [filteredOptions, activeIndex, value?.key, handleSelect],
  );

  return (
    <div ref={containerRef} className="relative w-full">
      <TextField
        ref={inputRef}
        variant={TextFieldVariant.Shadowed}
        placeholder={placeholder}
        value={inputValue}
        onChange={(event) => {
          setFilterValue(event.target.value);
          setIsSearching(true);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        prefix={
          <div className="flex h-full items-center pl-3">
            <Search size={16} className="text-gray-40" />
          </div>
        }
        postfix={
          value ? (
            <div className="flex h-full items-center pr-3">
              <button
                type="button"
                aria-label="Clear selection"
                className="flex items-center rounded-md hover:bg-gray-0"
                onClick={(event) => {
                  event.stopPropagation();
                  onChange(null);
                  setFilterValue("");
                  setIsSearching(false);
                }}
              >
                <X size={16} className="text-gray-40" />
              </button>
            </div>
          ) : undefined
        }
      />

      {isOpen && (
        <div className="ph-no-capture absolute z-[1400] mt-2 w-full rounded-md bg-white py-2 shadow-sm">
          {loading ? (
            <div className="flex items-center gap-x-2 p-3">
              <Spinner size="sm" className="text-gray-40" />
              <Text className="text-gray-40">{loadingText}</Text>
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="p-3">
              <Text className="text-gray-40">{noOptionsText}</Text>
            </div>
          ) : (
            <FixedSizeList
              ref={listRef}
              height={listHeight}
              itemCount={filteredOptions.length}
              itemSize={ROW_HEIGHT}
              width="100%"
              overscanCount={OVERSCAN}
              initialScrollOffset={initialScrollOffset}
            >
              {Row}
            </FixedSizeList>
          )}
        </div>
      )}
    </div>
  );
};

export default RemoteFieldTypeaheadV2;
